import React, { useMemo } from 'react';

import { Checkbox, FormGroup } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import { getEnvironment } from '../../../utils/environment';
import AdminAuthContainer from '../AdminAuthContainer';
import AdminSettingsContainer from '../AdminSettingsContainer';
import { H2, HelperText } from '../styles/headings';
import { showNonManagedZoomSetting } from '../utils/showAdvancedSetting';
import { AdminControlCheckbox } from '../utils/AdminControlCheckbox';

const StyledFormGroupContainer = styled.div`
    margin-left: 10px;
    margin-bottom: 28px;
`;

const ClassAccessSettings = () => {
    const { school, loadingSchool } = useContainer(AdminSettingsContainer);
    const { adminSettings, setAdminSettings } = useContainer(AdminSettingsContainer);
    const { isAdmin, isSuperAdmin } = useContainer(AdminAuthContainer);
    const { featureSettings } = adminSettings;
    const { t } = useTranslation('admin_settings');

    const nonManagedZoomSettingShown = useMemo(
        () =>
            showNonManagedZoomSetting({
                school,
                loadingSchool,
                isClassAdmin: isSuperAdmin
            }),
        [school, loadingSchool, isSuperAdmin]
    );

    const handleFeatureSettingsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        featureSettings[event.target.id] = event.target.checked;
        setAdminSettings({ ...adminSettings });
    };

    const { environment } = getEnvironment();

    return (
        <>
            <H2>{t('user-access.header')}</H2>
            <StyledFormGroupContainer>
                <FormGroup>
                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                color="primary"
                                checked={featureSettings.allowApp}
                                onChange={handleFeatureSettingsChange}
                                id="allowApp"
                                name="allowApp"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('user-access.allow-app') as string}
                    />
                </FormGroup>
                {nonManagedZoomSettingShown && (
                    <FormGroup>
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={featureSettings.allowZoom}
                                    onChange={handleFeatureSettingsChange}
                                    id="allowZoom"
                                    name="allowZoom"
                                    disabled={!isAdmin}
                                />
                            }
                            label={t('user-access.allow-launch-meeting-zoom.text') as string}
                        />
                        <HelperText htmlFor="allowZoom">
                            {t('user-access.allow-launch-meeting-zoom.helper-text')}
                        </HelperText>
                    </FormGroup>
                )}
                {/* @TODO --> chase: remove environment check when ready for test and prod */}
                {environment === 'dev' || environment === 'test' ? (
                    <FormGroup>
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={featureSettings.notifyOutdatedClassVersion}
                                    onChange={handleFeatureSettingsChange}
                                    id="notifyOutdatedClassVersion"
                                    name="notifyOutdatedClassVersion"
                                    disabled={!isAdmin}
                                />
                            }
                            label={t('user-access.notify-outdated-version') as string}
                        />
                    </FormGroup>
                ) : null}
            </StyledFormGroupContainer>
        </>
    );
};

export default ClassAccessSettings;
